<template>
  <div
    class="rounded-lg bg-surface border-main py-6 h-full"
    :class="hasKycDisabledClass"
  >
    <div class="lg:px-10 px-3">
      <div class="flex justify-between pb-5">
        <div>
          <p class="font-bold text-lg text-fg-default">Latest rate</p>
          <p class="font-normal text-fg-muted text-xs">
            Rates are updated every 30 minutes.
          </p>
        </div>
        <tooltip
          :disabled="hasCompletedKyc"
          placement="bottom"
          :content="disabledKycTooltipContent"
        >
          <div
            class="br-root-1 border rounded-lg w-20 lg:w-28 font-medium text-xs text-root-4 flex justify-center items-center cursor-pointer text-fg-default"
            v-on="hasCompletedKyc ? { click: () => showExchangeRates() } : {}"
          >
            See More
          </div>
        </tooltip>
      </div>
    </div>
    <div class="lg:px-10 px-3">
      <div class="flex justify-between items-center">
        <tooltip
          :disabled="hasCompletedKyc"
          placement="bottom"
          :content="disabledKycTooltipContent"
        >
          <div
            class="flex items-center gap-x-2.5 cursor-pointer"
            :class="hasKycDisabledClass"
            v-on="hasCompletedKyc ? { click: () => showExchangeRates() } : {}"
          >
            <div class="flex items-center gap-x-1">
              <currency-icons
                :currency="getCurrencyCountry(currentCurrencyPair.fromCurrency)"
              />
              <currency-icons
                :currency="getCurrencyCountry(currentCurrencyPair.toCurrency)"
              />
            </div>
            <div class="flex items-center">
              <p class="lg:text-lg text-base font-bold text-fg-default">
                {{ currentCurrencyPair.fromCurrency }}/{{
                  currentCurrencyPair.toCurrency
                }}
              </p>
              <arrow-right-icon />
            </div>
          </div>
        </tooltip>
        <div class="flex items-center">
          <p class="lg:text-xl text-lg font-extrabold mr-1 text-fg-default">
            {{ currencyUpdates.currentRate || 0 }}
          </p>
          <div
            class="rounded-xl px-1.5 text-center text-xs text-error-default font-medium bg-error-subtle py-1"
            :class="{ 'bg-green-6 text-green-5': !isNegativePercentageRate }"
          >
            {{ percentageChange || 0 }}%
          </div>
        </div>
      </div>
    </div>
    <div class="ml-4 mr-1">
      <div v-if="!isLoading" class="">
        <apexchart
          ref="realtimeChart"
          type="area"
          height="200"
          :options="chartOptions"
          :series="series"
        >
        </apexchart>
      </div>
      <p v-else class="text-root-2 my-4 text-xs">Fetching data...</p>
      <div class="flex items-center gap-x-4 px-3">
        <!-- deposit button -->
        <tooltip
          :disabled="hasCompletedKyc"
          placement="bottom"
          :content="disabledKycTooltipContent"
        >
          <div
            class="w-1/2 border border-primary text-root-11 py-4 text-center cursor-pointer font-medium lg:text-base text-xs rounded-lg"
            :class="hasKycDisabledClass"
            v-on="
              hasCompletedKyc && !isUserKindDeveloper
                ? { click: () => showExchangeRates() }
                : {}
            "
          >
            See all rates
          </div>
        </tooltip>

        <tooltip
          :disabled="hasCompletedKyc && hasDepositAccess"
          placement="bottom"
          :content="disabledTooltipContent"
        >
          <div
            class="w-1/2 cursor-pointer text-white bg-primary py-3 lg:py-4 text-center rounded-lg flex items-center font-medium lg:text-base text-xs lg:px-5 justify-center"
            :class="hasDisabledClass"
            v-on="
              hasCompletedKyc && hasDepositAccess
                ? { click: () => goToTerminal() }
                : {}
            "
          >
            Make transfer
            <span class="mt-0.5 lg:ml-3">
              <arrow-right-icon fill="black" useType="makeTransfer" />
            </span>
          </div>
        </tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { GetCurrencyCountry } from '@/utils/helpers.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  mixins: [userKinds],
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    ArrowRightIcon: () => import('@/assets/icons/ArrowRightIcon.vue')
  },
  data () {
    return {
      isLoading: false,
      disabledKycTooltipContent:
        'KYC is yet to be approved, currently under review',
      currencyOne: 'USD',
      currencyTwo: 'NGN',
      series: [
        {
          name: 'Exchange Rate',
          data: []
        }
      ],
      chartOptions: {
        chart: {
          toolbar: {
            show: false
          },
          type: 'area',
          stacked: false,
          height: 200,
          zoom: {
            enabled: false
          }
        },
        title: {
          text: '',
          align: 'right'
        },
        colors: ['#1570F2'],
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'straight',
          width: [2]
        },
        xaxis: {
          type: 'categories',
          categories: [],
          show: true,
          lines: {
            show: true
          },
          labels: {
            offsetX: 2,
            hideOverlappingLabels: false,
            trim: false,
            rotate: 0,
            show: true,
            maxHeight: 50,
            style: {
              colors: [],
              fontSize: '9px',
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 600,
              cssClass: 'apexcharts-xaxis-label'
            },
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm'
            }
          }
        },
        grid: {
          show: false,
          xaxis: {
            lines: {
              show: false
            }
          },
          colors: [this.$primaryColor],
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        yaxis: {
          opposite: true,
          show: false
        },
        legend: {
          horizontalAlign: 'right'
        }
      }
    }
  },
  watch: {
    '$store.state.report.currencyUpdates': function () {
      this.plotAreaChart()
      this.updateSeriesLine()
    }
  },
  methods: {
    setSeriesData () {
      this.series[0].data = this.rateChange.reverse().map((rate) => rate.rate)
    },
    setXaxisCategories () {
      const dates = this.rateChange.map((rate) =>
        this.$dayjs(rate.date).format('DD/MM')
      )
      const yMax = Math.max(...this.series[0].data)
      const yMin = Math.min(...this.series[0].data)
      this.chartOptions.yaxis.max = yMax
      this.chartOptions.yaxis.min = yMin - 5
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          xaxis: {
            categories: dates,
            type: 'categories'
          }
        }
      }
    },
    plotAreaChart () {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 0)
      this.setSeriesData()
      this.setXaxisCategories()
    },
    updateSeriesLine () {
      this.$refs.realtimeChart.updateSeries(
        [
          {
            data: this.series[0].data
          }
        ],
        false,
        true
      )
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    showExchangeRates () {
      this.$emit('showExchangeRates', 'exchange-rate-drawer-content')
    },
    goToTerminal () {
      this.$router.push('/terminal')
    },
    goToExchangeRate () {
      this.$router.push('/exchange-rates')
    }
  },
  computed: {
    hasKycDisabledClass () {
      return this.hasCompletedKyc && !this.isUserKindDeveloper
        ? 'opacity-100 hover:opacity-80'
        : 'cursor-not-allowed opacity-70 hover:opacity-70'
    },
    hasDisabledClass () {
      return this.hasCompletedKyc && this.hasApproverAccess
        ? 'opacity-100 hover:opacity-80'
        : 'cursor-not-allowed opacity-70 hover:opacity-70'
    },
    hasCompletedKyc () {
      return this.$store.state.auth.business.completedKyc
    },
    disabledTooltipContent () {
      return !this.hasCompletedKyc
        ? 'KYC is yet to be approved, currently under review'
        : 'You do not have the access for this transaction'
    },
    darkMode () {
      return this.$store.state.common.userTheme.includes('dark-theme')
    },
    rateChange () {
      return this.$store.state.report.currencyUpdates.rateChange || []
    },
    currencyUpdates () {
      return this.$store.state.report.currencyUpdates
    },
    percentageChange () {
      return this.currencyUpdates.percentageChange.toFixed(2)
    },
    isNegativePercentageRate () {
      return this.currencyUpdates.percentageChange < 0
    },
    currentCurrencyPair () {
      return this.$store.state.report.currentCurrencyPair
    }
  },
  async mounted () {
    await this.plotAreaChart()
  }
}
</script>

<style scoped>
>>> .apexcharts-xaxis-label {
  /* color: red !important;
  background: green !important;
  font-size: 20px !important; */
  z-index: 49 !important;
  display: absolute;
}
>>> .apexcharts-text tspan {
  color: var(--foreground-subtle);
}
</style>
